/**
 * Admin → Suggestions (PRP-MCP-12 work item 1, G3, founder decision F4): what
 * users sent from chat with suggest_improvement and share_macro. A shared macro
 * shows its lines and a Download .MCX button (the file for fixtures/macros/candidates/).
 *
 * Newest first, one row each: reference, date, user, kind, status, what they
 * asked. Filters in the URL: status, and a user (`/admin/suggestions?user=<id>`).
 * `?id=<suggestion id>` (the founder email's link) opens one. A suggestion shows
 * every field, the tool-call trail, the macro lines for a macro, a status
 * change and a reply box. Built or declined, or a reply, emails the user from
 * support@ with the reference; the server does that and says whether it went.
 * Admin only on the server; a non-admin gets 403 and sees the error.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { macroFileName, SuggestionsAPI } from '../../lib/suggestionsApi';
import type { SuggestionDetail, SuggestionRow, SuggestionStatus } from '../../lib/suggestionsApi';

const PAGE_SIZE = 50;

export const STATUS_LABEL: Record<SuggestionStatus, string> = {
  new: 'New',
  planned: 'Planned',
  built: 'Built',
  declined: 'Declined',
};
const STATUSES = Object.keys(STATUS_LABEL) as SuggestionStatus[];

const fmtTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '-');
const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '-' : String(v));

function errText(err: unknown, fallback: string): string {
  const e = err as { message?: string; status?: number };
  if (e?.status === 403) return 'Admins only.';
  return e?.message || fallback;
}

/** One suggestion: fields, trail, macro lines, status change and reply. */
const SuggestionView: React.FC<{
  detail: SuggestionDetail;
  onClose: () => void;
  onChanged: (d: SuggestionDetail, note: string) => void;
}> = ({ detail, onClose, onChanged }) => {
  const [status, setStatus] = useState<SuggestionStatus>(detail.status);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStatus(detail.status);
    setReply('');
  }, [detail.id, detail.status]);

  const emailedNote = (d: SuggestionDetail) => (d.emailed ? ' The user was emailed.' : '');

  const saveStatus = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await SuggestionsAPI.setStatus(detail.id, status, reply.trim() || undefined);
      onChanged(d, `${d.ref} is now ${STATUS_LABEL[d.status]}.${emailedNote(d)}`);
      setReply('');
    } catch (err) {
      setError(errText(err, 'Could not change the status.'));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const d = await SuggestionsAPI.reply(detail.id, reply.trim());
      onChanged(d, `Reply saved on ${d.ref}.${emailedNote(d)}`);
      setReply('');
    } catch (err) {
      setError(errText(err, 'Could not send the reply.'));
    } finally {
      setBusy(false);
    }
  };

  const downloadMacro = async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await SuggestionsAPI.macroFile(detail.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = macroFileName(detail.ref, detail.macro?.name);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(errText(err, 'Could not download the macro file.'));
    } finally {
      setBusy(false);
    }
  };

  const fields: [string, unknown][] = [
    ['What they asked for', detail.what_you_asked],
    ['What happened', detail.what_happened],
    ['What they expected', detail.what_you_expected],
    ['Estimate id', detail.estimate_id],
    ['Room', detail.room],
    ['Applies to', detail.applies_label || detail.applies],
    ['Notes', detail.notes],
    ['Chat app', detail.chat_app],
    ['After a nudge', detail.after_nudge ? 'Yes' : 'No'],
  ];
  const lines = detail.macro?.lines ?? [];

  return (
    <div className="space-y-4 rounded-md border border-border p-4" data-testid="suggestion-view">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">
          {detail.ref} · {STATUS_LABEL[detail.status]}
        </h2>
        <span className="text-sm text-muted-foreground">
          {fmtTime(detail.created_at)} ·{' '}
          <Link to={`/admin/users/${encodeURIComponent(detail.user_id)}`} className="underline">
            {detail.user_email || detail.user_id}
          </Link>{' '}
          · {detail.kind}
        </span>
        <button className="ml-auto rounded-md border border-border px-3 py-1 text-sm" onClick={onClose}>
          Close
        </button>
      </div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
        {fields.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="whitespace-pre-wrap">{dash(value)}</dd>
          </React.Fragment>
        ))}
      </dl>
      {detail.snapshot?.rooms?.length ? (
        <p className="text-xs text-muted-foreground">
          Room snapshot attached: {detail.snapshot.rooms.map((r) => `${r.name} (${r.lines.length} lines)`).join(', ')}.
        </p>
      ) : null}
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">Tool calls before it (newest first)</h3>
        {detail.trail.length === 0 ? (
          <p className="text-xs text-muted-foreground">None recorded.</p>
        ) : (
          <ol className="list-decimal pl-5 text-xs" data-testid="suggestion-trail">
            {detail.trail.map((t, i) => (
              <li key={`${t.tool}-${i}`}>
                <span className="font-mono">{t.tool}</span>: {t.outcome}
              </li>
            ))}
          </ol>
        )}
      </div>
      {detail.kind === 'macro' && (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">
              Macro lines{detail.macro?.name ? `: ${detail.macro.name}` : ''}
            </h3>
            {lines.length > 0 && (
              <button
                className="ml-auto rounded-md border border-border px-3 py-1 text-sm disabled:opacity-50"
                disabled={busy}
                onClick={() => void downloadMacro()}
              >
                Download .MCX
              </button>
            )}
          </div>
          {detail.macro?.description && (
            <p className="text-xs text-muted-foreground">{detail.macro.description}</p>
          )}
          <p className="text-xs text-muted-foreground">
            A copy the user sent, without line notes. Save the file into fixtures/macros/candidates/, or run
            scripts/mcp12_fetch_candidates.py to save every shared macro there.
          </p>
          {lines.length === 0 ? (
            <p className="text-xs text-muted-foreground">No lines.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="suggestion-macro-lines">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="p-1">Code</th>
                    <th className="p-1">Description</th>
                    <th className="p-1">Action</th>
                    <th className="p-1">Qty or calc</th>
                    <th className="p-1">Unit</th>
                    <th className="p-1">Heading</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln, i) => (
                    <tr key={i} className="border-t border-border align-top">
                      <td className="whitespace-nowrap p-1 font-mono">{`${ln.cat ?? ''} ${ln.sel ?? ''}`.trim()}</td>
                      <td className="p-1">{dash(ln.description)}</td>
                      <td className="p-1">{dash(ln.action)}</td>
                      <td className="p-1 font-mono">{dash(ln.calc ?? ln.quantity)}</td>
                      <td className="p-1">{dash(ln.unit)}</td>
                      <td className="p-1">{dash(ln.heading)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">Replies ({detail.replies.length})</h3>
        {detail.replies.map((r, i) => (
          <p key={i} className="whitespace-pre-wrap border-t border-border pt-1 text-xs">
            <span className="text-muted-foreground">{fmtTime(r.at)}{r.emailed ? ' · emailed' : ''}: </span>
            {r.text}
          </p>
        ))}
      </div>
      <div className="space-y-2">
        <textarea
          aria-label="Reply"
          className="w-full rounded-md border border-border bg-background p-2 text-sm"
          rows={3}
          placeholder="Reply to the user. Sent by email from support@ with the reference."
          value={reply}
          onChange={(e) => setReply(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="New status"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value as SuggestionStatus)}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
          <button
            className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
            disabled={busy || (status === detail.status && !reply.trim())}
            onClick={() => void saveStatus()}
          >
            Save status
          </button>
          <button
            className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
            disabled={busy || !reply.trim()}
            onClick={() => void sendReply()}
          >
            Send reply
          </button>
          <span className="text-xs text-muted-foreground">Built or declined emails the user.</span>
        </div>
        {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      </div>
    </div>
  );
};

/** The /admin/suggestions page. */
const AdminSuggestionsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as '' | SuggestionStatus;
  const user = params.get('user') ?? '';
  const openId = params.get('id') ?? '';
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<SuggestionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [detail, setDetail] = useState<SuggestionDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await SuggestionsAPI.list({
        ...(status ? { status } : {}),
        ...(user ? { user } : {}),
        limit: PAGE_SIZE,
        offset,
      });
      setRows(res.items ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      setError(errText(err, 'Could not load the suggestions.'));
    } finally {
      setLoading(false);
    }
  }, [status, user, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = useCallback(async (id: string) => {
    setError(null);
    try {
      setDetail(await SuggestionsAPI.get(id));
    } catch (err) {
      setError(errText(err, 'Could not open that suggestion.'));
    }
  }, []);

  useEffect(() => {
    if (openId) void open(openId);
  }, [openId, open]);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('id');
    setParams(next);
    setOffset(0);
    setDetail(null);
  };

  const changed = (d: SuggestionDetail, msg: string) => {
    setDetail(d);
    setNote(msg);
    void load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">Suggestions</h1>
        <Link to="/admin" className="text-sm text-muted-foreground underline">
          Admin
        </Link>
      </div>
      <p className="text-sm text-muted-foreground">
        What users sent from chat. Built or declined, or a reply, emails the user from support@ with the reference.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Status filter"
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={status}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          <option value="">Any status</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        {user && (
          <span className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-sm">
            User: {rows[0]?.user_email || user}
            <button aria-label="Clear user filter" className="text-muted-foreground" onClick={() => setFilter('user', '')}>
              ×
            </button>
          </span>
        )}
        <span className="text-sm text-muted-foreground">{loading ? 'Loading…' : `${total} suggestions`}</span>
      </div>
      {error && <p className="text-sm font-medium text-red-500">{error}</p>}
      {note && <p className="text-sm text-muted-foreground">{note}</p>}
      {detail && <SuggestionView detail={detail} onClose={() => setDetail(null)} onChanged={changed} />}
      {!loading && rows.length === 0 && !error ? (
        <p className="text-sm text-muted-foreground">No suggestions match.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-2">Ref</th>
                <th className="p-2">Date</th>
                <th className="p-2">User</th>
                <th className="p-2">Kind</th>
                <th className="p-2">Status</th>
                <th className="p-2">What they asked for</th>
                <th className="p-2">Replies</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-t border-border hover:bg-muted/30"
                  onClick={() => void open(r.id)}
                >
                  <td className="whitespace-nowrap p-2 font-mono">{r.ref}</td>
                  <td className="whitespace-nowrap p-2">{fmtTime(r.created_at)}</td>
                  <td className="p-2">
                    <button
                      className="underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilter('user', r.user_id);
                      }}
                    >
                      {r.user_email || r.user_id}
                    </button>
                  </td>
                  <td className="p-2">{r.kind}</td>
                  <td className="p-2">{STATUS_LABEL[r.status] ?? r.status}</td>
                  <td className="min-w-[16rem] p-2">{r.what_you_asked}</td>
                  <td className="p-2">{r.reply_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > PAGE_SIZE && (
        <div className="flex gap-2">
          <button
            className="rounded-md border border-border px-3 py-1 text-sm disabled:opacity-50"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </button>
          <button
            className="rounded-md border border-border px-3 py-1 text-sm disabled:opacity-50"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminSuggestionsPage;
